import { useMemo, useEffect, useState } from "react";
import { selectCurrentProject } from "@features/project/projectSlice";
import { useAppSelector, useThreadsProcessor } from "@app/hooks";
import { Formik, FormikHelpers } from "formik";
import { BEModulesClassNames } from "@utils/moduleList";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";

import { ModuleFull } from "./transportTypes";
import { useContextAwareGetTransport, useUpdateTransportsMutation, useUpdateTransportEntryMutation, useContextAwareGetTransportEntries } from "@hooks/index";
import { getSchemas, validationSchema, initialValues } from "./transport";
import { FEModules } from "@utils/moduleList";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import TransportTierOne from "./TransportTierOne";
import GenericTierTwo from "../GenericTierTwo";

const TransportModule = () => {
  const { activeActivityId } = useAppSelector(selectCurrentProject);
  const { moduleList } = useAppSelector(selectCurrentBuilder);
  const id = moduleList.find((module) => module.id === FEModules.Transport)?.uniqueId;

  const { data: transportsData, isLoading, isError, error } = useContextAwareGetTransport(id ?? 0, { refetchOnMountOrArgChange: true });
  const { data: entriesData, isLoading: isEntriesLoading, isError: isEntriesError, error: entriesError } = useContextAwareGetTransportEntries({ activityId: activeActivityId ?? 0 });
  const [updateTransports, { isError: isUpdateError, error: updateError }] = useUpdateTransportsMutation();
  const [updateTransportEntry, { isError: isUpdateTransportEntryError, error: updateTransportEntryError, isLoading: isUpdateTransportEntryLoading }] = useUpdateTransportEntryMutation();

  const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
  const [initValues, setInitValues] = useState<ModuleFull>(initialValues);
  const { errorMsg: updateParentErrorMsg, tabsOpen } = useModuleHook({
    skeletonsLoadingDeps: [isLoading, isEntriesLoading],
    isMutateError: isUpdateError,
    mutateError: updateError,
  });

  const { errorMsg: fetchError } = useErrorMessage({ isError: isEntriesError ?? isError, error: entriesError ?? error });
  const { errorMsg: updateEntryErrorMsg } = useErrorMessage({ isError: isUpdateTransportEntryError, error: updateTransportEntryError });
  const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

  useEffect(() => {
    if (!transportsData) return
    setInitValues((cur) => ({ ...cur, parent: transportsData }))
  }, [transportsData])

  useEffect(() => {
    if (entriesData)
      processModuleThreads({ transports: entriesData })
  }, [processModuleThreads, entriesData])

  const handleSubmit = async (values: ModuleFull, { resetForm }: FormikHelpers<ModuleFull>) => {
    const { transports, parent } = values
    try {
      const promises: Promise<any>[] = []
      const parentPayload = { body: { ...parent }, activityId: activeActivityId ?? 0 }

      transports.map((entry) => {
        const payload = { body: entry, activityId: activeActivityId ?? 0 }
        if (entry.id) promises.push(updateTransportEntry(payload))
        return Promise.resolve()
      })

      await Promise.all(promises);
      await new Promise(resolve => setTimeout(resolve, 500));
      if (parent.id) updateTransports(parentPayload)
      resetForm({ values })
    } catch (error) {
      console.error(error)
    }
    resetForm({ values })
  }

  return (
    <Formik
      initialValues={initValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      <>
        <TransportTierOne
          title="project.transport"
          initValues={initValues}
          t2Schema={t2Schema}
          beModuleType={BEModulesClassNames.Transport}
          moduleSchema={moduleSchema}
          isLoading={isLoading || isEntriesLoading || isUpdateTransportEntryLoading}
          footerError={updateParentErrorMsg ?? updateEntryErrorMsg}
          blockingError={fetchError}
          isEntriesLoading={isEntriesLoading}
          setInitValues={setInitValues} />
        <GenericTierTwo
          title="project.transport"
          t2Schema={t2Schema}
          tabsOpen={tabsOpen}
        />
      </>
    </Formik>
  );
};

export default TransportModule;
